import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Helmet } from "react-helmet-async";
import { Loader2 } from "lucide-react";

import { PageHeader } from "@/components/ui/page-header";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { GroupSearchCombobox } from "@/components/ui/group-search-combobox";
import { LeaderboardTable } from "@/components/portal/lms/LeaderboardTable";
import { johannesburgMonth, PERIOD_LABEL_PATTERN } from "@/components/portal/lms/periods";
import { useReviewDirectory } from "@/components/portal/lms/useReviewDirectory";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { useAuth } from "@/contexts/AuthContext";
import { categoriesApi, type CategoryHierarchyNode } from "@/services/api/categories";
import { LEADERBOARD_TOP_DEFAULT, lmsLeaderboardApi } from "@/services/api/lms";

export const Route = createFileRoute("/portal/lms/leaderboard")({
  component: LmsLeaderboardPage,
});

const OVERALL = "__overall__";
const TOP_CHOICES = [10, 25, 50] as const;

function flattenCategories(nodes: CategoryHierarchyNode[], depth = 0): { id: string; name: string; depth: number }[] {
  return nodes.flatMap((n) => [
    ...(n.is_accessible === false ? [] : [{ id: n.id, name: n.name, depth }]),
    ...flattenCategories(n.children ?? [], depth + 1),
  ]);
}

/**
 * Per-team leaderboard (`/portal/lms/leaderboard`): Top-N and the caller's own
 * rank for one Johannesburg month, overall or for one category.
 *
 * The team picker is built from dp's `GET /leaderboards/teams` — the inverse of
 * the board gate — so it offers only boards the caller may read: one team is
 * auto-selected, several are a labelled select, and an org admin (bypass) picks
 * any team. Any API refusal (403 included) is shown verbatim.
 */
function LmsLeaderboardPage() {
  const { token } = useAuth();
  const { selectedOrganisation } = usePortalData();
  const orgId = selectedOrganisation?.organisationId ?? "";
  const directory = useReviewDirectory();

  const [teamId, setTeamId] = useState<string>("");
  const [period, setPeriod] = useState(() => johannesburgMonth(new Date()));
  const [categoryId, setCategoryId] = useState<string>(OVERALL);
  const [top, setTop] = useState<number>(LEADERBOARD_TOP_DEFAULT);

  const teams = useQuery({
    queryKey: ["lms", "leaderboard-teams", orgId],
    queryFn: () => lmsLeaderboardApi.listTeams(token ?? ""),
    enabled: !!token && !!orgId,
    retry: false,
  });
  const teamIds = useMemo(() => teams.data?.team_ids ?? [], [teams.data]);
  const bypass = teams.data?.bypasses_groups === true;

  // One readable team: select it. Several: the first, until the caller picks.
  useEffect(() => {
    if (!bypass && teamIds.length > 0 && !teamIds.includes(teamId)) setTeamId(teamIds[0]);
  }, [bypass, teamIds, teamId]);

  const hierarchy = useQuery({
    queryKey: ["lms", "category-picker", orgId],
    queryFn: () => categoriesApi.hierarchy.get(token ?? "", orgId, true),
    enabled: !!token && !!orgId,
  });
  const categories = useMemo(() => flattenCategories(hierarchy.data?.root_categories ?? []), [hierarchy.data]);

  const periodValid = PERIOD_LABEL_PATTERN.test(period);
  const topicCategoryId = categoryId === OVERALL ? null : categoryId;
  const board = useQuery({
    queryKey: ["lms", "leaderboard", orgId, teamId, period, topicCategoryId, top],
    queryFn: () => lmsLeaderboardApi.getTeamBoard(token ?? "", teamId, { period, topicCategoryId, top }),
    enabled: !!token && !!teamId && periodValid,
    retry: false,
  });

  return (
    <div className="space-y-4" data-testid="lms-leaderboard-page">
      <Helmet>
        <title>Leaderboards | LMS</title>
      </Helmet>
      <PageHeader title="Leaderboards" description="A team's Top-N and your own rank for a month, overall or per category." />

      {teams.isLoading ? (
        <div className="flex justify-center py-12 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : teams.error ? (
        <p className="text-sm text-destructive" role="alert" data-testid="lms-leaderboard-error">
          {(teams.error as Error).message}
        </p>
      ) : !bypass && teamIds.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="lms-leaderboard-no-teams">
          You have no team boards yet. A board appears once you sit a test on a team, or manage one.
        </p>
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-4">
            <div className="min-w-[220px] space-y-1.5" data-testid="lms-leaderboard-team">
              <Label>Team</Label>
              {bypass ? (
                <GroupSearchCombobox
                  token={token ?? ""}
                  organisationId={orgId}
                  value={teamId || undefined}
                  onValueChange={(id) => setTeamId(id)}
                  placeholder="Search teams…"
                />
              ) : teamIds.length === 1 ? (
                <p className="py-2 text-sm font-medium">{directory.teamName(teamIds[0])}</p>
              ) : (
                <Select value={teamId} onValueChange={setTeamId}>
                  <SelectTrigger data-testid="lms-leaderboard-team-select">
                    <SelectValue placeholder="Select a team" />
                  </SelectTrigger>
                  <SelectContent>
                    {teamIds.map((id) => (
                      <SelectItem key={id} value={id}>
                        {directory.teamName(id)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="lms-leaderboard-period">Month</Label>
              <Input
                id="lms-leaderboard-period"
                type="month"
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                data-testid="lms-leaderboard-period"
              />
            </div>
            <div className="min-w-[200px] space-y-1.5">
              <Label>Category</Label>
              <Select value={categoryId} onValueChange={setCategoryId}>
                <SelectTrigger data-testid="lms-leaderboard-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={OVERALL}>Overall</SelectItem>
                  {categories.map((c) => (
                    <SelectItem key={c.id} value={c.id} style={{ paddingLeft: `${8 + c.depth * 16}px` }}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Show</Label>
              <Select value={String(top)} onValueChange={(v) => setTop(Number(v))}>
                <SelectTrigger className="w-[110px]" data-testid="lms-leaderboard-top">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TOP_CHOICES.map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      Top {n}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {!teamId ? (
            <p className="text-sm text-muted-foreground">Pick a team to see its board.</p>
          ) : !periodValid ? (
            <p className="text-sm text-muted-foreground">Pick a month.</p>
          ) : board.isLoading ? (
            <div className="flex justify-center py-12 text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : board.error ? (
            <p className="text-sm text-destructive" role="alert" data-testid="lms-leaderboard-error">
              {(board.error as Error).message}
            </p>
          ) : board.data ? (
            <LeaderboardTable board={board.data} />
          ) : null}
        </>
      )}
    </div>
  );
}
